<template>
  <div id="app" class="container-fluid">
    <full_head />

    <div class="main-content">
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading newborn information...</p>
      </div>

      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="fetchRecords">Retry</button>
      </div>

      <div v-else class="card">
        <div class="card-header">
          <h3><i class="fas fa-baby"></i> Newborn Information</h3>
          <button class="btn-edit-header" @click="openAddModal">
            <i class="fas fa-plus"></i> Add Newborn
          </button>
        </div>
        <div class="card-body">
          <div v-if="!records.length" class="no-data">
            No newborn(s) recorded for this visit yet.
          </div>
          <div v-else class="newborn-grid">
            <div class="newborn-card" v-for="record in records" :key="record.id">
              <div class="newborn-card-header">
                <div>
                  <strong>{{ record.birth_order || "Newborn" }} — {{ record.sex || "Sex unspecified" }}</strong>
                  <span
                    class="badge-outcome"
                    :class="record.birth_outcome === 'Live birth' ? 'outcome-live' : 'outcome-other'"
                    >{{ record.birth_outcome }}</span
                  >
                </div>
                <div>
                  <button class="btn-icon" title="Edit" @click="openEditModal(record)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="btn-icon btn-icon-danger" title="Delete" @click="deleteRecord(record.id)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <div class="newborn-body">
                <div class="newborn-row">
                  <span v-if="record.birth_weight_grams"><strong>Weight:</strong> {{ record.birth_weight_grams }} g</span>
                  <span v-if="record.birth_length_cm"><strong>Length:</strong> {{ record.birth_length_cm }} cm</span>
                  <span v-if="record.head_circumference_cm"><strong>HC:</strong> {{ record.head_circumference_cm }} cm</span>
                </div>
                <div class="newborn-row" v-if="record.apgar_1min || record.apgar_5min || record.apgar_10min">
                  <span><strong>APGAR:</strong> {{ record.apgar_1min ?? "-" }} / {{ record.apgar_5min ?? "-" }} / {{ record.apgar_10min ?? "-" }}</span>
                </div>
                <div class="newborn-flags">
                  <span v-if="record.resuscitation_required" class="flag flag-danger">Resuscitated</span>
                  <span v-if="record.vitamin_k_given" class="flag">Vit K</span>
                  <span v-if="record.eye_care_given" class="flag">Eye Care</span>
                  <span v-if="record.cord_care_given" class="flag">Cord Care</span>
                  <span v-if="record.bcg_given" class="flag">BCG</span>
                  <span v-if="record.opv0_given" class="flag">OPV0</span>
                  <span v-if="record.breastfeeding_initiated" class="flag flag-ok">Breastfeeding started</span>
                </div>
                <p v-if="record.congenital_abnormalities" class="newborn-text">
                  <strong>Abnormalities:</strong> {{ record.congenital_abnormalities }}
                </p>
                <p v-if="record.notes" class="newborn-text"><strong>Notes:</strong> {{ record.notes }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <NewbornModal
      v-if="showModal"
      :visitId="visit_id"
      :editRecord="editingRecord"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import full_head from "./full_head.vue";
import NewbornModal from "./NewbornModal.vue";
import { generalApiRequest } from "../../../../../api/apiIndex";

export default {
  name: "MaternityNewborn",
  components: { full_head, NewbornModal },
  data() {
    return {
      loading: false,
      error: null,
      visit_id: null,
      records: [],
      showModal: false,
      editingRecord: null,
    };
  },
  mounted() {
    this.visit_id = this.$route.params.visit_id;
    if (this.visit_id) {
      this.fetchRecords();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    async fetchRecords() {
      this.loading = true;
      this.error = null;
      try {
        const response = await generalApiRequest(`newborns/visit/${this.visit_id}`, "GET", {}, 1);
        if (response.success) {
          this.records = response.data || [];
        } else {
          this.error = response.message || "Failed to load newborn information";
        }
      } catch (err) {
        console.error("Error fetching newborn records:", err);
        this.error = "An error occurred while loading newborn information. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.editingRecord = null;
      this.showModal = true;
    },
    openEditModal(record) {
      this.editingRecord = record;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingRecord = null;
    },
    async handleSaved() {
      this.closeModal();
      await this.fetchRecords();
    },
    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this newborn record?")) return;
      try {
        const response = await generalApiRequest(`newborns/${id}`, "DELETE", {}, 1);
        if (response.success) {
          this.records = this.records.filter((r) => r.id !== id);
        } else {
          alert(response.message || "Failed to delete newborn record");
        }
      } catch (error) {
        console.error("Error deleting newborn record:", error);
        alert("Failed to delete newborn record. Please try again.");
      }
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.container-fluid { padding: 12px; background-color: #f5f7fa; min-height: 100vh; }
.main-content { margin-top: 12px; }
.loading-container, .error-container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center; background: white; border-radius: 10px; }
.loading-container i { font-size: 3rem; color: #dd6b20; margin-bottom: 1rem; }
.error-container i { font-size: 3rem; color: #dc3545; margin-bottom: 1rem; }
.btn-retry { background: #dd6b20; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-weight: 600; cursor: pointer; }
.card { background: white; border-radius: 10px; border: 1px solid #e9ecef; box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
.card-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-bottom: 2px solid #dee2e6; }
.card-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #495057; display: flex; align-items: center; gap: 8px; }
.card-header h3 i { color: #dd6b20; }
.btn-edit-header { background: linear-gradient(135deg, #f6ad55 0%, #dd6b20 100%); color: white; border: none; padding: 7px 16px; border-radius: 6px; font-weight: 600; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.card-body { padding: 16px 20px; }
.no-data { text-align: center; padding: 20px; color: #6c757d; font-size: 13px; font-style: italic; background: #f8f9fa; border-radius: 6px; }
.newborn-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 14px; }
.newborn-card { border: 1px solid #e9ecef; border-radius: 8px; overflow: hidden; }
.newborn-card-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 10px 14px; background: #f8f9fa; border-bottom: 1px solid #e9ecef; }
.badge-outcome { margin-left: 8px; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; }
.outcome-live { background: #d4edda; color: #155724; }
.outcome-other { background: #f8d7da; color: #721c24; }
.btn-icon { background: none; border: none; color: #dd6b20; cursor: pointer; padding: 4px 6px; }
.btn-icon-danger { color: #dc3545; }
.newborn-body { padding: 12px 14px; }
.newborn-row { display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; color: #495057; margin-bottom: 8px; }
.newborn-flags { display: flex; gap: 6px; margin-bottom: 8px; flex-wrap: wrap; }
.flag { background: #fff3cd; color: #856404; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.flag-danger { background: #f8d7da; color: #721c24; }
.flag-ok { background: #d4edda; color: #155724; }
.newborn-text { font-size: 13px; color: #495057; margin: 4px 0; }
</style>
